import { ROUTES } from "../../constants/routes";
import { Component, inject } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { RouterLink } from "@angular/router";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { SITE, STATS } from "../../constants/site";
import { ICONS } from "../../constants/icons";
import { COMPANY_FACTS, FOUNDER, initialsFor, PUBLISHED_LEADERSHIP } from "../../constants/team";
import {
  breadcrumbSchema,
  leadershipSchema,
  organizationSchema,
  webPageSchema
} from "../../helpers/structured-data";

@Component({
  selector: "app-about",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./about.html",
  styleUrl: "./about.scss"
})
export class About {
  readonly routes = ROUTES;
  private readonly seo = inject(SeoService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly site = SITE;
  readonly stats = STATS;
  readonly leadership = PUBLISHED_LEADERSHIP;
  readonly companyFacts = COMPANY_FACTS;
  readonly founder = FOUNDER;

  initials(name: string): string {
    return initialsFor(name);
  }

  iconFor(name: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(ICONS[name] ?? "");
  }

  readonly story = [
    {
      year: SITE.researchSince,
      title: "It started with obituaries, not fraud",
      text: "Our team set out to find inheritance related real estate and connect heirs of estates with professionals who could help them. Doing that well meant one unglamorous habit: reading obituaries, every single day, in every state.",
      highlight: ""
    },
    {
      year: "The next decade",
      title: "A pattern we could not ignore",
      text: `Somewhere past the twenty millionth record, the same story kept repeating. A person passes away. Their bank, their employer, their insurer and their lender carry on as if nothing happened, because nobody tells them.`,
      highlight: ""
    },
    {
      year: "The gap",
      title: `The first ${STATS.highRiskWindowDays} days are where the damage happens`,
      text: "Official death files can take months to surface a passing. In that silence a card gets opened in the deceased person's name, a pension keeps paying, a claim gets filed, or a grieving family gets a collections call about someone they just buried.",
      highlight: `${STATS.highRiskWindowDays} days of silence is all a fraudster needs.`
    },
    {
      year: "Today",
      title: "The research operation became a product",
      text: `We already had the people, the sources and the daily process. VerifyDeceased puts that behind an API and a file upload, running on the same data engine as ${SITE.parent} and its 160 million property records.`,
      highlight: ""
    }
  ];

  datasetItems = [
    {
      icon: "database",
      title: "Verified deceased individual records",
      description:
        "Name, date of birth, date of death and age, confirmed before the record is released to you."
    },
    {
      icon: "pin",
      title: "Confirmed addresses, with or without property",
      description:
        "Last known address on file even when the person never owned a home, which is where most deceased data sets fall short."
    },
    {
      icon: "users",
      title: "Optional relationship data on request",
      description:
        "Heirs, surviving spouses and next of kin contact details can be appended where you have a permissible purpose for them."
    }
  ];

  outcomes = [
    "Reduce identity theft and death related fraud",
    "Flag likely deceased records sitting in your customer database",
    "Protect families from post mortem financial exploitation",
    "Support compliance, investigations and risk management",
    "Help families and estate professionals find accurate information"
  ];

  fraudTypes = [
    {
      icon: "briefcase",
      title: "Employment fraud",
      description:
        "A stolen identity is used to pass onboarding and payroll checks at a real employer."
    },
    {
      icon: "card",
      title: "Credit card and loan fraud",
      description:
        "New lines are opened in the name of someone who cannot dispute them or answer a fraud call."
    },
    {
      icon: "bank",
      title: "Bank account fraud",
      description: "Existing accounts are drained or repurposed before the estate is settled."
    },
    {
      icon: "landmark",
      title: "Government benefit fraud",
      description: "Payments keep flowing to a person who is no longer alive to receive them."
    }
  ];

  values = [
    {
      title: "Accuracy",
      description:
        "Every record is confirmed before release and every match carries a confidence score, so you always know how much weight to put on it."
    },
    {
      title: "Transparency",
      description:
        "Clear per record pricing, no minimums, a published security posture and a documented account of where our data comes from."
    },
    {
      title: "Innovation",
      description:
        "More than a decade of obituary research turned into a verification engine you can call from a file upload or a single API request."
    }
  ];

  ngOnInit(): void {
    this.seo.apply({
      title: "About VerifyDeceased | America's Source of Truth for Deceased Verification",
      description:
        "VerifyDeceased was built on more than a decade of obituary research covering 30 million records. Learn how we confirm deceased identities and stop death related fraud.",
      path: "/about",
      keywords:
        "about VerifyDeceased, deceased individual database, death record verification company, deceased identity fraud, LeadCruncher death data",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" }
        ]),
        webPageSchema({
          name: "About VerifyDeceased",
          description:
            "VerifyDeceased is a deceased identity verification service built on more than a decade of nationwide obituary and death record research.",
          path: "/about",
          type: "AboutPage"
        }),
        organizationSchema(),
        ...(this.leadership.length ? [leadershipSchema(this.leadership)] : [])
      ]
    });
  }
}
